import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { WidthObserver } from '@atlaskit/width-detector';

import { ITEM, type LinksMenuParameters, type Styles } from '../../../../types';

import { MenuItem } from './menu-item';
import { MenuLinkItem } from './menu-link-item';
import { messages } from './messages';

type LinksMenuItemsProps = Pick<LinksMenuParameters, 'items'> &
	Pick<Styles, 'textColor' | 'highlightColor'>;

const APPROX_OVERFLOW_BUTTON_WIDTH = 100;

const APPROX_MIN_PADDING_INLINE = 48;

function howManyTabsFit(individualTabWidths: number[], availableWidth: number) {
	let numberTabs = 0,
		totalWidthTabs = 0;

	for (let i = 0; i < individualTabWidths.length; i++) {
		totalWidthTabs += individualTabWidths[i];
		if (totalWidthTabs > availableWidth) {
			break;
		}
		numberTabs++;
	}
	return { numberTabs };
}

function newItemTabWidthsRef() {
	const current = new Array<number>();

	return {
		current,

		// Identifies to React Compiler that the reactive state is the opaque value "item tab widths ref", its internal mutations are purposefully not reactive
		setItemTabWidth(index: number, tabWidth: number) {
			current[index] = tabWidth;
		},
	};
}

export const LinksMenuItems = ({
	highlightColor,
	items,
	textColor,
}: LinksMenuItemsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const [maxLinksMenuTabs, setMaxLinksMenuTabs] = useState(items?.length || 0);
	const itemTabWidthsOld = useRef<number[]>([]);
	// `itemTabWidths` used to be a `useRef`, but that turned out to be insufficienlty reactive, re-calculations were not triggered though were necessary.
	// `useState` allows for more control over hook re-executions and rerenders than `useRef`,
	// while `newItemTabWidthsRef` allows for preventing unnecessary hook re-executions and re-renders upon `handleSetWebItemTabWidth`.
	const [itemTabWidthsNew, setItemTabWidths] = useState(newItemTabWidthsRef);
	const itemTabWidths = fg('pcs-3808077-links-menu-react-compiler-fixes')
		? itemTabWidthsNew
		: itemTabWidthsOld;
	const [width, setWidth] = useState<number>(0);

	useLayoutEffect(() => {
		// calculate how many tabs fit given current width on first render, and on resize
		const availableWidth = Math.max(
			width - APPROX_OVERFLOW_BUTTON_WIDTH - 2 * APPROX_MIN_PADDING_INLINE,
			0,
		);
		if (availableWidth) {
			const { numberTabs } = howManyTabsFit(itemTabWidths.current, availableWidth);

			setMaxLinksMenuTabs(numberTabs);
		}
		// run this calculation also when maxLinksMenuTabs updates
	}, [itemTabWidths, width, maxLinksMenuTabs]);

	useEffect(() => {
		// Handle when `items` updates, eg link or menu items are added or removed in config panel and
		// we need to recalculate width.

		// Reset itemTabWidths to empty when `items` updates so that we can calculate all widths from
		// scratch, otherwise adding/deleting items can cause the widths array to fall out of sync as
		// elements move indexes in the items array
		if (fg('pcs-3808077-links-menu-react-compiler-fixes')) {
			setItemTabWidths(newItemTabWidthsRef);
		} else {
			itemTabWidthsOld.current = [];
		}

		// Resetting max # of tabs here will cause a brief rendering of ALL tabs and they will all call the
		// setWidth callback and update itemTabWidths array with their respective widths.
		// A new value for maxLinksMenuTabs will also cause useLayoutEffect to rerun, recalculate available width,
		// and reset maxLinksMenuTabs again which will send tabs that can't fit to the overflow menu.
		setMaxLinksMenuTabs(items?.length || 0);
	}, [items]);

	const handleSetWebItemTabWidthNew: NonNullable<ComponentProps<typeof MenuItem>['setWidth']> =
		useCallback(
			(tabWidth, index) => {
				if (index != null) {
					itemTabWidthsNew.setItemTabWidth(index, tabWidth);
				}
			},
			[itemTabWidthsNew],
		);

	if (!items) {
		return null;
	}

	const handleSetWebItemTabWidthOld: typeof handleSetWebItemTabWidthNew = (tabWidth, index) => {
		if (index != null) {
			itemTabWidthsOld.current[index] = tabWidth;
		}
	};

	const handleSetWebItemTabWidth = fg('pcs-3808077-links-menu-react-compiler-fixes')
		? handleSetWebItemTabWidthNew
		: handleSetWebItemTabWidthOld;

	const onResize = debounce(setWidth, 100, { leading: false });

	const isOverflowDetected = items.length > maxLinksMenuTabs;

	return (
		<>
			{items?.slice(0, maxLinksMenuTabs).map((item, index) => {
				const { itemId, type, title } = item;
				if (type === ITEM.MENU) {
					return (
						<MenuItem
							index={index}
							key={index}
							title={title}
							childItems={item.childItems}
							highlightColor={highlightColor}
							textColor={textColor}
							isFirstItem
							setWidth={handleSetWebItemTabWidth}
						/>
					);
				}
				return (
					<MenuLinkItem
						index={index}
						itemId={itemId}
						key={index}
						link={item.link}
						textColor={textColor}
						title={title}
						setWidth={handleSetWebItemTabWidth}
					/>
				);
			})}
			{isOverflowDetected && (
				<MenuItem
					title={formatMessage(messages.moreItemTitle)}
					childItems={items?.slice(maxLinksMenuTabs - items.length)}
					highlightColor={highlightColor}
					textColor={textColor}
					isFirstItem
				/>
			)}
			<WidthObserver setWidth={onResize} />
		</>
	);
};
